// DescriptionList + Field — the shared label→value display for detail pages, peeks,
// and review steps. Replaces the hand-rolled `<dl>` + dt/dd blocks that had drifted
// across users / roles / projects + the workbench stories (COMPONENTS §Catalog).
//
// One row = a `Field` (dt label at start, dd value at inline-end, baseline-aligned).
// The list divides rows with a hairline by default (`divided`); pass `divided={false}`
// for a flush group. Values are any ReactNode (text, a Badge, an EntityRef); `detail`
// is the value's lighter second line (a qualifier — a time zone, a count, who did it).

import type { ReactNode } from 'react';

export function DescriptionList({
  children,
  divided = true,
  className = '',
}: {
  children: ReactNode;
  /** Hairline dividers between rows (default true). */
  divided?: boolean;
  className?: string;
}) {
  return (
    <dl className={`${divided ? 'divide-y divide-[var(--color-divider)]' : ''} ${className}`}>{children}</dl>
  );
}

export function Field({
  label,
  children,
  detail,
}: {
  label: ReactNode;
  children: ReactNode;
  /** The value's lighter second line — a qualifier of the fact (a time zone, a count, who did it), never a second fact. */
  detail?: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="shrink-0 text-sm text-[var(--color-text-secondary)]">{label}</dt>
      <dd className="min-w-0 text-end text-sm font-medium text-[var(--color-text-primary)]">
        {children}
        {detail !== undefined && detail !== null && <span className="block text-xs font-normal text-[var(--color-text-secondary)]">{detail}</span>}
      </dd>
    </div>
  );
}
